import {Injectable} from '@angular/core';
import {Form} from '../interfaces/entities/form';
import {SectionItem} from '../interfaces/form/section-item';
import {FieldItem} from '../interfaces/form/field-item';
import {FormArray, FormBuilder, FormGroup, Validators, ValidatorFn, AbstractControl, ValidationErrors} from '@angular/forms';
import {Field} from '../interfaces';
import {FieldSection} from "@app/interfaces/fields/field.section";
import {environment} from "@src/environments/environment";

@Injectable({
  providedIn: 'root'
})
export class FormHelperService {

  constructor(private formBuilder: FormBuilder) {
  }

  /**
   * Restricts a shared, multi-tab settings Form to just the given top-level tab keys.
   *
   * Some settings forms (e.g. Virtual Salon's) are split across more than one page, each
   * page rendering only a subset of the backend's tabs (BRIEFD-7363: the Publishing page
   * shows general/autopublish/publishing-rules, the Lead Generation page shows the rest).
   * Without this, a page that calls toTabbedForm() on the FULL unfiltered form builds
   * validity around tabs it never even renders — a stale/invalid value in a tab the user
   * can't see on THIS page (e.g. a malformed leads_email saved via the other page) makes
   * formGroup.invalid permanently true here, with no field visible to fix it.
   */
  scopeFormToTabs(form: Form, tabKeys: string[]): Form {
    const layouts = form.layouts.filter((layout) => tabKeys.includes((layout as SectionItem).key));

    // Dev-only signal for backend drift (a renamed/added groupKey) that would otherwise
    // silently drop a card from the page with no visible error - the tab just vanishes,
    // and since these settings are only editable through these scoped pages, that field
    // becomes permanently unreachable until someone notices.
    if (!environment.production && layouts.length !== tabKeys.length) {
      const foundKeys = layouts.map((layout) => (layout as SectionItem).key);
      console.warn('scopeFormToTabs: no layout found for tab key(s)', tabKeys.filter((key) => !foundKeys.includes(key)));
    }

    return { ...form, layouts };
  }

  toTabbedForm(form: Form): FormGroup {
    const groups = {};
    // BRIEFD-7306: a fieldUuid can now appear in more than one tab's layout
    // (e.g. buy price / bruto-neto checkbox render in both Osnovne and
    // Nabava). Track every control created per fieldUuid so they can be
    // wired together below — each tab still gets its own independent
    // AbstractControl instance (Angular reactive forms don't support one
    // control belonging to two parents), so without this tracking + sync
    // step, editing the value in one tab wouldn't show up in the other.
    const controlsByFieldUuid: {[fieldUuid: string]: AbstractControl[]} = {};

    form.layouts.forEach((sectionItem: SectionItem, i) => groups[i] = this.extractFieldsPerTabs(sectionItem));
    for (const group in groups) {
      if (groups.hasOwnProperty(group)) {
        const controls = {};

        for (const fieldItem of groups[group]) {
          const fieldUuid = (fieldItem as FieldItem).fieldUuid;
          const field = form.fields[fieldUuid];
          const control = this.prepareFormGroupControlForFieldItem(field);

          controls[fieldUuid] = control;
          (controlsByFieldUuid[fieldUuid] ??= []).push(control);
        }

        groups[group.toString()] = this.formBuilder.group(controls);
      }
    }

    this.syncDuplicateFieldControls(controlsByFieldUuid);

    return this.formBuilder.group(groups);
  }

  /**
   * The FormGroup for a single tab (section), so one tab can be rebuilt from
   * fresh backend data without touching the others — used when the advertising
   * tab is re-derived from unsaved vehicle data (BRIEFD-7189).
   */
  toSectionForm(section: SectionItem, fields: { [uuid: string]: Field<any> }): FormGroup {
    const controls = {};

    for (const fieldItem of this.extractFieldsPerTabs(section)) {
      const field = fields[(fieldItem as FieldItem).fieldUuid];
      if (field) {
        controls[(fieldItem as FieldItem).fieldUuid] = this.prepareFormGroupControlForFieldItem(field);
      }
    }

    return this.formBuilder.group(controls);
  }

  /** Every field uuid rendered inside a section, including nested groups. */
  sectionFieldUuids(section: SectionItem): string[] {
    return this.extractFieldsPerTabs(section).map((item: FieldItem) => item.fieldUuid);
  }

  /**
   * BRIEFD-7306: mirrors every fieldUuid that got more than one independent
   * control (i.e. it's placed in more than one tab) so they always agree,
   * live — not just at save time. Without this, `getValuesFromTabbedForm()`'s
   * merge below (which walks tabs in order and lets a later tab's value win)
   * would silently discard an edit made in an earlier tab whenever the later
   * tab's copy of the same field was never touched by the user. Keeping both
   * copies in sync as the user types makes that merge safe again, since by
   * save time every duplicate holds the same value regardless of tab order.
   *
   * `{emitEvent: false}` on the mirrored write is what stops this from
   * ping-ponging between the two subscriptions.
   */
  private syncDuplicateFieldControls(controlsByFieldUuid: {[fieldUuid: string]: AbstractControl[]}): void {
    for (const fieldUuid of Object.keys(controlsByFieldUuid)) {
      const controls = controlsByFieldUuid[fieldUuid];
      if (controls.length < 2) {
        continue;
      }

      controls.forEach((control, index) => {
        const others = controls.filter((_, otherIndex) => otherIndex !== index);
        control.valueChanges.subscribe((value) => {
          for (const other of others) {
            if (other.value !== value) {
              other.setValue(value, {emitEvent: false});
            }
          }
        });
      });
    }
  }

  toTabbedFormInline(sections: FieldSection[]): FormGroup {
    const controls = {};

    for (const section of sections) {
      for (const field of section.items) {
        controls[field.fieldUuid] = this.prepareFormGroupControlForFieldItem(field);
      }
    }

    return this.formBuilder.group(controls);
  }

  /**
   * Generic cross-field date validator
   * @param compareToField - fieldUuid to compare to
   * @param compareType - 'mustBeAfter' | 'mustBeBefore'
   */
  public static crossFieldDateValidator(compareToField: string, compareType: 'mustBeAfter' | 'mustBeBefore'): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.parent || !compareToField || !compareType) return null;
      const compareControl = control.parent.get(compareToField);
      if (!compareControl || !control.value || !compareControl.value) return null;
      const thisDate = new Date(control.value);
      const otherDate = new Date(compareControl.value);
      if (compareType === 'mustBeAfter' && thisDate <= otherDate) {
        return { mustBeAfter: true };
      }
      if (compareType === 'mustBeBefore' && thisDate >= otherDate) {
        return { mustBeBefore: true };
      }
      return null;
    };
  }

  /**
   * Validator to ensure only URLs are present in the text
   * Accepts URLs with or without protocol (e.g. www.example.com, http://example.com)
   * Accepts separation by comma, space, or newline
   */
  public static allowOnlyUrlsValidator(): ValidatorFn {
    // Accepts URLs with or without protocol
    const urlRegex = /^(https?:\/\/)?([\w-]+\.)+[\w-]{2,}(\/[\w\-.,@?^=%&:/~+#]*)?(\?[\w\-.,@?^=%&:/~+#]*)?$/i;
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null;
      // Split by comma, newlines, or spaces
      const lines = String(control.value).split(/,|\r?\n|\s+/).map(s => s.trim()).filter(Boolean);
      const allUrls = lines.every(line => urlRegex.test(line));
      return allUrls ? null : { allowOnlyUrls: true };
    };
  }

  public prepareFormGroupControlForFieldItem(field: Field<any>) {

    if (field.type === 'multi_checkbox') {
      return this.buildCheckboxes(field, this.formBuilder.group({}));
    } else if (field.type === 'repeater') {
      return this.buildRepeater(field, this.formBuilder.array([]));
    }

    const validators = this.fillValidators(field);

    // Add cross-field date validator if config is present
    if (field.type === 'date_time' && field.config.compareToField && field.config.compareType) {
      validators.push(FormHelperService.crossFieldDateValidator(field.config.compareToField, field.config.compareType));
    }
    // Add allowOnlyUrls validator if config is present
    if (field.type === 'textblock' && field.config.allowOnlyUrls) {
      validators.push(FormHelperService.allowOnlyUrlsValidator());
    }
    return this.formBuilder.control(FormHelperService.sanitizeFieldValue(field), validators);
  }

  /**
   * Defensive prefill: when the BE hands back a value that can't possibly
   * satisfy the field's own validators (e.g. a non-numeric string in a
   * `number` field), strip it on the way in.
   *
   * Why: a previous write (import, legacy save, migration) sometimes left
   * non-numeric strings like `"1.lastnik"` in `entity_data` for fields
   * whose declared type is `number`. The `<input type="number">` can't
   * display that value, so the user sees an empty field — but the
   * underlying control still holds the bad string, which the next save
   * sends back to the BE, which 422s with "must be a number" pointing at
   * a field the user didn't visibly touch. Clearing the value during the
   * initial bind makes the displayed state and the stored state agree:
   * empty looks empty, and saving an empty optional field passes.
   */
  static sanitizeFieldValue(field: Field<any>): unknown {
    const value = field?.value;
    if (value === null || value === undefined) { return value; }

    const numericTypes = new Set(['number', 'currency', 'mileage', 'percent']);
    if (!numericTypes.has(field?.type)) { return value; }

    // Already a finite number — pass through.
    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : null;
    }
    // String that parses cleanly to a number — coerce so downstream
    // consumers don't have to guess at the type.
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed === '') { return null; }
      const parsed = Number(trimmed);
      return Number.isFinite(parsed) ? parsed : null;
    }
    // Anything else (object/array) on a numeric field is malformed —
    // drop it. The BE never legitimately stores those shapes for these
    // field types.
    return null;
  }

  private buildCheckboxes(currentValue: Field<any>, control: FormGroup) {
    const sections = {};

    for (const section of currentValue.config.sections) {
      const controls = {};

      for (const item of section.items) {
        if (currentValue.value && currentValue.value[section.section_uuid]) {
          controls[Object.keys(item)[0]] = this.formBuilder.control(currentValue.value[section.section_uuid][Object.keys(item)[0]]);
        } else {
          controls[Object.keys(item)[0]] = this.formBuilder.control(false);
        }
      }

      sections[section.section_uuid] = this.formBuilder.group(controls);
    }

    for (const section of Object.keys(sections)) {
      control.addControl(section, sections[section]);
    }

    return control;
  }

  private extractFieldsPerTabs(sectionItem: SectionItem) {
    let fields = [];

    for (const item of sectionItem.items) {
      if (item.type === 'field') {
        fields.push(item);
      } else {
        fields = fields.concat(this.extractFieldsPerTabs(item as SectionItem));
      }
    }

    return fields;
  }

  private fillValidators(field: Field<any>) {
    const validators = [];

    if (field.validators?.includes('required')) {
      validators.push(Validators.required);
    }

    if (['number', 'currency', 'mileage', 'repeater', 'percent'].indexOf(field.type) !== -1) {
      const min = field.validators?.find((item) => item.includes('min:'));
      const max = field.validators?.find((item) => item.includes('max:'));

      if (min) {
        validators.push(Validators.min(parseFloat(min.substr(4))));
      }
      if (field.config.min != null) {
        validators.push(Validators.min(field.config.min));
      }

      if (max) {
        validators.push(Validators.max(parseFloat(max.substr(4))));
      }
      if (field.config.max != null) {
        validators.push(Validators.max(field.config.max));
      }
    }

    if (['files', 'photos', 'public_photos', 'nlb_files', 'proxy_photos'].indexOf(field.type) !== -1) {
      const min = field.validators?.find((item) => item.includes('min:'));
      const max = field.validators?.find((item) => item.includes('max:'));

      if (min) {
        validators.push(Validators.minLength(parseFloat(min.substr(4))));
        validators.push(Validators.required);
      }

      if (max) {
        validators.push(Validators.maxLength(parseFloat(max.substr(4))));
      }
    }

    if (field.config['regex']) {
      validators.push(Validators.pattern(field.config.regex));
    }

    return validators;
  }

  /**
   * Last-tab-wins by construction (a later tab's value always overwrites an
   * earlier tab's for the same key). That's only safe for a fieldUuid shared
   * across tabs because `toTabbedForm()`'s `syncDuplicateFieldControls()`
   * keeps every duplicate control's value identical live — see BRIEFD-7306.
   */
  getValuesFromTabbedForm(formGroup: FormGroup): { [key: string]: any } {
    const tabs = Object.keys(formGroup.getRawValue());
    return tabs.reduce((values, tab) => ({...values, ...formGroup.getRawValue()[tab]}), {});
  }

  findControl(customId: any, control: any) {
    let parent = control;

    while (parent.parent) {
      parent = parent.parent;
    }

    return this.findControlInControls(customId, parent.controls);
  }

  private findControlInControls(customId: string, controls) {
    const keys = Object.keys(controls);

    if (keys.indexOf(customId) !== -1) {
      return controls[customId];
    }

    for (const key of keys) {

      const control = controls[key];

      if (control.controls) {

        const result = this.findControlInControls(customId, control.controls);

        if (result) {

          return result;
        }
      }
    }

    return null;
  }

  public buildRepeater(field: Field<any>, formArray: FormArray) {
    if (!field.value) {
      return formArray;
    }

    for (const wrappedItem of field.value) {
      const repeaterItem = this.prepareEmptyRepeaterItem(field);
      repeaterItem.controls.wrappedItemUuid.setValue(wrappedItem.wrappedItemUuid);

      for (const fieldItem of wrappedItem.value) {
        (repeaterItem.controls.value as FormGroup).controls[fieldItem.fieldUuid].setValue(fieldItem.value);
      }

      formArray.push(repeaterItem);
    }

    const validators = [];

    if (field.config?.min) {
      validators.push(Validators.minLength(field.config.min));
    }

    if (field.config?.isRequired) {
      validators.push(Validators.required);
    }

    if (field.config?.max) {
      validators.push(Validators.maxLength(field.config.max));
    }

    formArray.setValidators(validators);

    return formArray;
  }

  public prepareEmptyRepeaterItem(field: Field<any>, blankValues = false): FormGroup {
    const controls = {};

    for (const fieldUuid in field.config.subFields) {
      if (field.config.subFields.hasOwnProperty(fieldUuid)) {
        const subField = field.config.subFields[fieldUuid];
        // A brand-new repeater row must not inherit the shared sub-field
        // config's `value` (left over from the previously edited row), which
        // made e.g. a new damage/expense start with the previous cost.
        controls[fieldUuid] = this.prepareFormGroupControlForFieldItem(
          blankValues ? {...subField, value: null} : subField
        );
      }
    }

    return this.formBuilder.group({
      wrappedItemUuid: this.formBuilder.control(null),
      value: this.formBuilder.group(controls)
    });
  }

  modifyOnSetFields(value: any, field: Field<any>, formGroup: FormGroup, booleanValue = false) {
    if (!value || !field.config.hasOwnProperty('onSelect')) {
      return;
    }

    const selectedTypeId = booleanValue ? value : Object.keys(value)[0];
    // Use specific config if it exists, otherwise fall back to _default (but don't merge them)
    const selectedConfig = field.config.onSelect[selectedTypeId] || field.config.onSelect._default || {};
    const fieldsToHide = selectedConfig.hideFields || [];
    const fieldsToShow = selectedConfig.showFields || [];
    const fieldsToRequire = selectedConfig.requiredFields || [];
    const fieldsToNotRequire = selectedConfig.notRequiredFields || [];

    for (const fieldValue of fieldsToHide) {
      if (formGroup.controls[fieldValue]) {
        // This is done because the abstractControl doesn't have a hidden attribute
        // @ts-ignore
        formGroup.controls[fieldValue].hidden = true;
        formGroup.controls[fieldValue].disable();
      } else {
        console.error("The field " + fieldValue + " doesn't exists.");
      }
    }

    for (const fieldValue of fieldsToShow) {
      if (formGroup.controls[fieldValue]) {
        // This is a hack because the abstractControl doesn't have a hidden attribute
        // @ts-ignore
        formGroup.controls[fieldValue].hidden = false;
        formGroup.controls[fieldValue].enable();
      } else {
        console.error("The field " + fieldValue + " doesn't exists.");
      }
    }

    for (const fieldValue of fieldsToRequire) {
      if (formGroup.controls[fieldValue]) {
        // This is a hack because the abstractControl doesn't have a hidden attribute
        formGroup.controls[fieldValue].setValidators([Validators.required]);
        formGroup.controls[fieldValue].updateValueAndValidity({emitEvent: true});
      } else {
        console.error("The field " + fieldValue + " doesn't exists.");
      }
    }

    let isRequired = selectedConfig.requiredFields || [];

    for (const fieldValue of fieldsToNotRequire) {
      if (formGroup.controls[fieldValue]) {
        if(!isRequired.includes(fieldValue)){
          // This is a hack because the abstractControl doesn't have a hidden attribute
          // @ts-ignore
          formGroup.controls[fieldValue].removeValidators([Validators.required]);
          formGroup.controls[fieldValue].updateValueAndValidity({emitEvent: true});
        }
      } else {
        console.error("The field " + fieldValue + " doesn't exists.");
      }
    }
  }
}
